import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core-tab-bar';
import '@maersk-global/mds-components-core-tab';
import '@maersk-global/mds-components-core-accordion';
import '@maersk-global/mds-components-core-accordion-item';
import '@maersk-global/mds-components-core-button';
import '@maersk-global/mds-components-core-toast';
import '@maersk-global/mds-components-core-notification';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-option';
import '@maersk-global/mds-components-core/mc-icon';
import { VscKpiBinningTableComponent } from '../../components/vsc/vsc-kpi-binning-table/vsc-kpi-binning-table.component';
import { VscSettingsProfilesComponent } from '../../components/vsc/vsc-settings-profiles/vsc-settings-profiles.component';
import { VscKpiWeightsComponent } from '../../components/vsc/vsc-kpi-weights/vsc-kpi-weights.component';
import { VscProfileMembersComponent } from '../../components/vsc/vsc-profile-members/vsc-profile-members.component';
import {
  CreateVendorSettingsRequest,
  KpiCategoryResponse,
  ScoreBand,
  ScoringEngineRunRequest,
  UpdateVendorSettingsRequest,
  VendorSpecificSettingsApiResponse,
  VscKpiGroup,
  VscKpiItem,
} from '../../models/vsc-settings.model';
import { VscKpiCategoryService } from '../../services/vsc-kpi-category.service';
import { AuthHelper } from '../../auth/auth-helper';
import { AuthPolicies } from '../../auth/auth-policies';
import { ToastrService } from '../../services/toastr-service';
import settingsData from '../../data/vsc-settings.data.json';

@Component({
  selector: 'app-vsc-settings',
  imports: [CommonModule, VscKpiBinningTableComponent, VscSettingsProfilesComponent, VscKpiWeightsComponent, VscProfileMembersComponent],
  templateUrl: './vsc-settings.component.html',
  styleUrl: './vsc-settings.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscSettingsComponent implements OnInit {
  @ViewChild('saveToastRef') saveToastRef!: ElementRef;
  @ViewChild(VscSettingsProfilesComponent) profilesRef!: VscSettingsProfilesComponent;

  private readonly kpiCategoryService = inject(VscKpiCategoryService);
  private readonly toastrService = inject(ToastrService);
  readonly authPolicy = inject(AuthPolicies);

  kpiGroups: VscKpiGroup[] = settingsData.kpiGroups;

  /** KPI groups loaded from API — used for global settings binning tab */
  apiKpiGroups: VscKpiGroup[] = [];

  kpiWeights: Record<string, string> = {};
  categoryWeights: Record<string, string> = {};
  selectedProfileId: number | null = null;
  selectedProfile: VendorSpecificSettingsApiResponse | null = null;
  activeTabIndex = 0;
  isSaving = false;
  isCreateCopyMode = false;
  showDeleteModal = false;
  isDeleting = false;

  /** ── On-demand scoring run ── */
  showCalculateModal = false;
  isCalculating = false;
  /** True when the run covers every vendor (global settings tab) rather than one setting's vendors */
  calculateAllVendors = false;
  /** Selected combined periods, each keyed as `<month>-<year>` — e.g. "jul-2026" — newest first */
  calculatePeriods: string[] = [];
  /** Selectable periods, newest first — rebuilt each time the dialog opens */
  periodOptions: { value: string; label: string }[] = [];
  /**
   * One entry per period submitted, in run order — the run is sequential (the API only accepts a
   * single month/year per call) and this list is how the dialog shows progress as each completes,
   * before the summary toast fires at the end.
   */
  calculateStatuses: { value: string; label: string; state: 'pending' | 'success' | 'error' }[] = [];
  readonly monthOptions: { value: string; label: string }[] = [
    { value: 'jan', label: 'Jan' },
    { value: 'feb', label: 'Feb' },
    { value: 'mar', label: 'Mar' },
    { value: 'apr', label: 'Apr' },
    { value: 'may', label: 'May' },
    { value: 'jun', label: 'Jun' },
    { value: 'jul', label: 'Jul' },
    { value: 'aug', label: 'Aug' },
    { value: 'sep', label: 'Sep' },
    { value: 'oct', label: 'Oct' },
    { value: 'nov', label: 'Nov' },
    { value: 'dec', label: 'Dec' },
  ];

  /** Tracks current vendor/tag lists as edited by VscProfileMembersComponent */
  currentVendorIds: string[] = [];
  currentTags: string[] = [];

  /** Stable reference — KPI groups as flat items for the vendor category binning table */
  kpiGroupsAsItems: VscKpiItem[] = [];
  /** Updated only when categoryWeights changes */
  disabledBinningIds: string[] = [];
  /** Updated only when categoryWeights changes */
  maxScore5ForBinning: Record<string, string> = {};

  get canManageVscSettings(): boolean {
    return this.authPolicy.isUserGSCorAdminAccess;
  }

  /** Category score bands as edited in the binning table — sent with Save, never posted on their own. */
  private editedCategoryBands: Record<string, ScoreBand[]> = {};
  /** True while the binning table holds an invalid range, which blocks Save. */
  binningInvalid = false;

  ngOnInit(): void {
    for (const group of this.kpiGroups) {
      this.categoryWeights[group.id] = String(group.defaultCategoryWeight);
      for (const kpi of group.kpis) {
        this.kpiWeights[kpi.id] = String(kpi.defaultWeight);
      }
    }
    // Computed once — kpiGroups never changes after init
    this.kpiGroupsAsItems = this.kpiGroups.map(({ id, label }) => ({ id, label, defaultWeight: 0 }));
    this.recomputeBinningDerived();
    this.loadApiKpiGroups();
  }

  private loadApiKpiGroups(): void {
    this.kpiCategoryService.getKpiCategories().subscribe({
      next: (categories) => {
        this.apiKpiGroups = this.mapApiToKpiGroups(categories);
      },
    });
  }

  private mapApiToKpiGroups(categories: KpiCategoryResponse[]): VscKpiGroup[] {
    return categories.map((cat) => ({
      id: String(cat.id),
      label: cat.name,
      defaultCategoryWeight: 0,
      kpis: cat.kpis.map((kpi) => ({
        id: String(kpi.id),
        label: kpi.name,
        defaultWeight: 0,
        unit: kpi.unit,
        isLowerBetter: kpi.isLowerBetter,
        scoreBands: kpi.scoreBands,
      })),
    }));
  }

  private recomputeBinningDerived(): void {
    this.disabledBinningIds = this.kpiGroups
      .filter((g) => !this.categoryWeights[g.id] || Number.parseFloat(this.categoryWeights[g.id]) === 0)
      .map((g) => g.id);
    this.maxScore5ForBinning = Object.fromEntries(
      this.kpiGroups
        .filter((g) => this.categoryWeights[g.id] && Number.parseFloat(this.categoryWeights[g.id]) > 0)
        .map((g) => [g.id, this.categoryWeights[g.id]]),
    );
  }

  get catTotal(): number {
    return this.kpiGroups.reduce((acc, g) => {
      const v = Number.parseFloat(this.categoryWeights[g.id] || '0');
      return acc + (Number.isNaN(v) ? 0 : v);
    }, 0);
  }

  /** True when category total ≠ 100 or any active category's KPI sum ≠ its category weight */
  get weightsInvalid(): boolean {
    if (this.catTotal !== 100) return true;
    return this.kpiGroups.some((g) => {
      const catW = Number.parseFloat(this.categoryWeights[g.id] || '0') || 0;
      if (catW === 0) return false;
      const sum = g.kpis.reduce((acc, kpi) => {
        const v = Number.parseFloat(this.kpiWeights[kpi.id]);
        return acc + (Number.isNaN(v) ? 0 : v);
      }, 0);
      return sum !== catW;
    });
  }

  onTabChange(event: Event): void {
    this.activeTabIndex = (event as CustomEvent).detail as number;
    if (this.activeTabIndex === 0) this.loadApiKpiGroups();
    this.selectedProfileId = null;
    this.selectedProfile = null;
    this.kpiGroups = settingsData.kpiGroups;
    this.kpiWeights = {};
    this.categoryWeights = {};
    this.kpiGroupsAsItems = [];
    this.disabledBinningIds = [];
    this.maxScore5ForBinning = {};
    this.currentVendorIds = [];
    this.currentTags = [];
    this.isCreateCopyMode = false;
    this.resetBinningEdits();
  }

  onProfileDeleted(): void {
    this.selectedProfileId = null;
    this.selectedProfile = null;
    this.currentVendorIds = [];
    this.currentTags = [];
    this.isCreateCopyMode = false;
    this.resetBinningEdits();
  }

  /** The table republishes both as soon as it renders the next profile. */
  private resetBinningEdits(): void {
    this.editedCategoryBands = {};
    this.binningInvalid = false;
  }

  onCreateCopy(profile: VendorSpecificSettingsApiResponse): void {
    this.isCreateCopyMode = true;
    this.selectedProfileId = profile.id;
    this.selectedProfile = profile;
    // Start with empty tags and vendor IDs for the copy
    this.currentVendorIds = [];
    this.currentTags = [];
    this.loadProfileConfig(profile);
    this.profilesRef?.insertDummyCard();
  }

  onProfileSelect(profile: VendorSpecificSettingsApiResponse): void {
    this.isCreateCopyMode = false;
    this.selectedProfileId = profile.id;
    this.selectedProfile = profile;
    this.currentVendorIds = [...profile.vendorIds];
    this.currentTags = [...profile.tags];
    this.loadProfileConfig(profile);
  }

  private loadProfileConfig(profile: VendorSpecificSettingsApiResponse): void {
    const categories = profile.config.kpiCategories;

    this.kpiGroups = categories.map((cat) => ({
      id: String(cat.id),
      label: cat.name,
      defaultCategoryWeight: cat.weight,
      kpis: cat.kpis.map((kpi) => ({
        id: String(kpi.id),
        label: kpi.name,
        defaultWeight: kpi.weight,
      })),
    }));

    this.categoryWeights = Object.fromEntries(categories.map((cat) => [String(cat.id), String(cat.weight)]));

    this.kpiWeights = Object.fromEntries(categories.flatMap((cat) => cat.kpis.map((kpi) => [String(kpi.id), String(kpi.weight)])));

    this.kpiGroupsAsItems = categories.map((cat) => ({
      id: String(cat.id),
      label: cat.name,
      defaultWeight: cat.weight,
      scoreBands: cat.band,
    }));
    this.resetBinningEdits();
    this.recomputeBinningDerived();
  }

  onKpiWeightsChange(weights: Record<string, string>): void {
    this.kpiWeights = weights;
  }

  onCategoryWeightsChange(weights: Record<string, string>): void {
    this.categoryWeights = weights;
    this.recomputeBinningDerived();
  }

  onCategoryBandsChange(bands: Record<string, ScoreBand[]>): void {
    this.editedCategoryBands = bands;
  }

  onCategoryBinningValidityChange(invalid: boolean): void {
    this.binningInvalid = invalid;
  }

  get isCurrentProfileDefault(): boolean {
    return this.selectedProfile?.tags.includes('default') ?? false;
  }

  get isProfileReadOnly(): boolean {
    if (!this.canManageVscSettings) {
      return true;
    }

    return !this.isCreateCopyMode && (this.selectedProfile?.tags.includes('default') ?? false);
  }

  confirmDelete(): void {
    if (!this.selectedProfileId || this.isDeleting) return;
    const user = AuthHelper.user;
    const deletedId = this.selectedProfileId;
    this.isDeleting = true;
    this.kpiCategoryService.deleteVendorSettings(deletedId, user?.userId ?? 0, new Date().toISOString()).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteModal = false;
        this.profilesRef.slectedSettingsId = 0;
        this.profilesRef.reload();
        this.onProfileDeleted();
      },
      error: () => {
        this.isDeleting = false;
        this.showDeleteModal = false;
        this.toastrService.showToastr('Failed to delete VSC settings. Please try again.', 'error');
      },
    });
  }

  /**
   * Scores are only complete once a month has closed out: from the 12th onwards the previous month
   * is ready, before that only the month before it.
   */
  private defaultScoringPeriod(now = new Date()): { month: string; year: number } {
    const monthsBack = now.getDate() >= 12 ? 1 : 2;
    const target = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
    return { month: this.monthOptions[target.getMonth()].value, year: target.getFullYear() };
  }

  /**
   * Periods offered in the dialog, newest first: from the latest closed-out month back to January
   * of the previous year, so only the current and previous year are ever selectable.
   */
  private buildPeriodOptions(latestMonth: string, latestYear: number): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [];
    const earliest = new Date(new Date().getFullYear() - 1, 0, 1);
    let cursor = new Date(
      latestYear,
      this.monthOptions.findIndex((m) => m.value === latestMonth),
      1,
    );

    while (cursor >= earliest) {
      const month = this.monthOptions[cursor.getMonth()];
      const year = cursor.getFullYear();
      options.push({ value: `${month.value}-${year}`, label: `${month.label} ${year}` });
      cursor = new Date(year, cursor.getMonth() - 1, 1);
    }

    return options;
  }

  /** `allVendors` runs the engine for every vendor, which the API expresses as an empty vendorIds list. */
  openCalculateModal(allVendors = false): void {
    const { month, year } = this.defaultScoringPeriod();
    this.calculateAllVendors = allVendors;
    this.calculatePeriods = [`${month}-${year}`];
    this.periodOptions = this.buildPeriodOptions(month, year);
    this.calculateStatuses = [];
    this.showCalculateModal = true;
  }

  onCalculatePeriodChange(event: Event): void {
    const detail = (event as CustomEvent).detail as { value: string }[] | undefined;
    if (!detail) return;
    this.calculatePeriods = detail.map((option) => option.value);
  }

  /** Vendors the selected setting applies to — the same list shown under Vendor Codes. */
  get calculateVendorIds(): string[] {
    return this.currentVendorIds;
  }

  /** True while the dialog cannot be submitted — no period selected, or a per-setting run with no vendors. */
  get calculateDisabled(): boolean {
    if (this.calculatePeriods.length === 0) return true;
    return !this.calculateAllVendors && this.calculateVendorIds.length === 0;
  }

  private periodLabel(value: string): string {
    return this.periodOptions.find((option) => option.value === value)?.label ?? value;
  }

  /**
   * The API scores one month/year per call, so a multi-month run is submitted sequentially — one
   * request at a time, in the order picked. Each result updates that period's row in the dialog
   * immediately, so the user sees progress before the summary toast fires at the end.
   */
  confirmCalculate(): void {
    if (this.isCalculating || this.calculateDisabled) return;

    this.calculateStatuses = this.calculatePeriods.map((value) => ({ value, label: this.periodLabel(value), state: 'pending' }));
    this.isCalculating = true;
    this.runCalculateStep(0);
  }

  private runCalculateStep(index: number): void {
    if (index >= this.calculateStatuses.length) {
      this.finishCalculate();
      return;
    }

    const [month, year] = this.calculateStatuses[index].value.split('-');
    const payload: ScoringEngineRunRequest = {
      month,
      year: Number(year),
      // An empty list tells the engine to score every vendor.
      vendorIds: this.calculateAllVendors ? [] : [...this.calculateVendorIds],
    };

    this.kpiCategoryService.runScoringEngine(payload).subscribe({
      next: () => {
        this.calculateStatuses[index] = { ...this.calculateStatuses[index], state: 'success' };
        this.runCalculateStep(index + 1);
      },
      error: () => {
        this.calculateStatuses[index] = { ...this.calculateStatuses[index], state: 'error' };
        this.runCalculateStep(index + 1);
      },
    });
  }

  /** Closes the dialog and toasts only once every period has run, success or failure. */
  private finishCalculate(): void {
    this.isCalculating = false;
    const scope = this.calculateAllVendors ? 'all vendors' : `${this.calculateVendorIds.length} vendor(s)`;
    const failed = this.calculateStatuses.filter((status) => status.state === 'error');

    if (failed.length === 0) {
      this.showCalculateModal = false;
      const periodsLabel = this.calculateStatuses.map((status) => status.label).join(', ');
      this.toastrService.showToastr(`Score calculation completed for ${scope} for ${periodsLabel}.`, 'success');
      return;
    }

    const failedLabel = failed.map((status) => status.label).join(', ');
    this.toastrService.showToastr(`Failed to calculate scores for ${failedLabel}. Please try again.`, 'error');
  }

  get saveDisabled(): boolean {
    if (this.isSaving || this.weightsInvalid || this.binningInvalid) return true;
    return this.currentTags.length === 0 || this.currentVendorIds.length === 0;
  }

  /** Why Save is greyed out, in one line. Empty while Save is available. */
  get saveDisabledReason(): string {
    if (this.isSaving) return '';
    if (this.weightsInvalid) return 'Category weights must total 100, and each category’s KPI weights must add up to its weight.';
    if (this.binningInvalid) return 'Fix the highlighted score ranges under KPI Category Score Binning.';

    const missingTags = this.currentTags.length === 0;
    const missingVendors = this.currentVendorIds.length === 0;
    if (missingTags && missingVendors) return 'Add at least one tag and one vendor code.';
    if (missingTags) return 'Add at least one tag.';
    if (missingVendors) return 'Add at least one vendor code.';

    return '';
  }

  save(): void {
    if (!this.selectedProfile || this.isSaving) return;

    const user = AuthHelper.user;
    const config = {
      version: this.selectedProfile.config.version,
      kpiCategories: this.kpiGroups.map((group) => ({
        id: Number(group.id),
        name: group.label,
        weight: Number(this.categoryWeights[group.id] ?? 0),
        band: this.editedCategoryBands[group.id] ?? this.kpiGroupsAsItems.find((item) => item.id === group.id)?.scoreBands ?? [],
        kpis: group.kpis.map((kpi) => ({
          id: Number(kpi.id),
          name: kpi.label,
          weight: Number(this.kpiWeights[kpi.id] ?? 0),
        })),
      })),
    };

    this.isSaving = true;

    if (this.isCreateCopyMode) {
      const createPayload: CreateVendorSettingsRequest = {
        id: null,
        config,
        vendorIds: this.currentVendorIds,
        tags: this.currentTags,
        createdById: user?.userId ?? 0,
        createdAt: new Date().toISOString(),
      };
      this.kpiCategoryService.createVendorSettings(createPayload).subscribe({
        next: () => {
          this.isSaving = false;
          this.onProfileDeleted();
          this.profilesRef.reload();
          this.saveToastRef?.nativeElement?.show();
        },
        error: () => {
          this.isSaving = false;
          this.toastrService.showToastr('Failed to create VSC settings. Please try again.', 'error');
        },
      });
    } else {
      const updatePayload: UpdateVendorSettingsRequest = {
        id: this.selectedProfile.id,
        config,
        vendorIds: this.currentVendorIds,
        tags: this.currentTags,
        createdById: user?.userId ?? 0,
        createdAt: new Date().toISOString(),
      };
      this.kpiCategoryService.updateVendorSettings(updatePayload).subscribe({
        next: () => {
          this.isSaving = false;
          this.onProfileDeleted();
          this.profilesRef.reload();
          this.saveToastRef?.nativeElement?.show();
        },
        error: () => {
          this.isSaving = false;
          this.toastrService.showToastr('Failed to save VSC settings. Please try again.', 'error');
        },
      });
    }
  }
}
